import { appendSocialShareUtmParams } from '../../../lib/analytics/utmParams';

/** Default message shown when sharing persisted public match results. */
export const MATCH_RESULTS_SHARE_MESSAGE =
  'Check out my personal match results';

export const MATCH_RESULTS_SHARE_TITLE = 'My pet match results';

export type MatchResultsSharePayload = {
  url: string;
  title?: string;
  text?: string;
};

export type SocialSharePlatform = 'facebook' | 'instagram' | 'tiktok';

export type SocialShareOutcome =
  | 'shared'
  | 'copied'
  | 'aborted'
  | 'failed'
  | 'web';

export function buildMatchResultsSharePayload(
  url: string,
  overrides?: { title?: string; text?: string },
): MatchResultsSharePayload {
  return {
    url,
    title: overrides?.title ?? MATCH_RESULTS_SHARE_TITLE,
    text: overrides?.text ?? MATCH_RESULTS_SHARE_MESSAGE,
  };
}

/** Returns share payload with UTM params for GA4 social attribution. */
export function buildTaggedSocialSharePayload(
  publicShareUrl: string,
  platform: 'facebook' | 'instagram' | 'tiktok',
  overrides?: { title?: string; text?: string },
): MatchResultsSharePayload {
  return buildMatchResultsSharePayload(
    appendSocialShareUtmParams(publicShareUrl, platform),
    overrides,
  );
}

/** Text + URL for clipboard when a platform has no web share composer. */
export function formatMatchResultsShareClipboard(
  payload: MatchResultsSharePayload,
): string {
  const lines = [payload.text?.trim(), payload.url.trim()].filter(Boolean);
  return lines.join('\n\n');
}

/** Facebook sharer — `quote` is best-effort; FB often uses link preview only. */
export function buildFacebookShareUrl(payload: MatchResultsSharePayload): string {
  const u = encodeURIComponent(payload.url);
  const quote = encodeURIComponent(payload.text ?? MATCH_RESULTS_SHARE_MESSAGE);
  return `https://www.facebook.com/sharer/sharer.php?u=${u}&quote=${quote}`;
}

function platformLabel(platform: SocialSharePlatform): string {
  switch (platform) {
    case 'facebook':
      return 'Facebook';
    case 'instagram':
      return 'Instagram';
    case 'tiktok':
      return 'TikTok';
  }
}

/** Mobile browsers hijack sharer.php into the native FB app without a working composer. */
function canUseFacebookWebSharer(): boolean {
  if (typeof navigator === 'undefined') return false;
  return !/iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
}

async function tryNativeShare(
  shareVariants: ShareData[],
): Promise<'shared' | 'aborted' | 'unavailable'> {
  if (typeof navigator === 'undefined' || typeof navigator.share !== 'function') {
    return 'unavailable';
  }

  for (const data of shareVariants) {
    try {
      if (
        typeof navigator.canShare === 'function' &&
        !navigator.canShare(data)
      ) {
        continue;
      }
      await navigator.share(data);
      return 'shared';
    } catch (error) {
      const err = error as { name?: string };
      if (err?.name === 'AbortError') return 'aborted';
    }
  }

  return 'unavailable';
}

/**
 * Social share: Web Share API first (required on mobile iOS for Facebook), then
 * desktop-only sharer.php for Facebook, then clipboard with instructions.
 */
export async function shareMatchResultsToSocialPlatform(
  platform: SocialSharePlatform,
  publicShareUrl: string,
): Promise<SocialShareOutcome> {
  const payload = buildTaggedSocialSharePayload(publicShareUrl, platform);
  const clipboardText = formatMatchResultsShareClipboard(payload);

  const shareVariants: ShareData[] = [
    { title: payload.title, text: payload.text, url: payload.url },
    { text: clipboardText, url: payload.url },
    { url: payload.url },
    { text: clipboardText },
  ];

  const nativeOutcome = await tryNativeShare(shareVariants);
  if (nativeOutcome === 'shared' || nativeOutcome === 'aborted') {
    return nativeOutcome;
  }

  if (
    platform === 'facebook' &&
    canUseFacebookWebSharer() &&
    typeof window !== 'undefined'
  ) {
    window.open(
      buildFacebookShareUrl(payload),
      '_blank',
      'noopener,noreferrer',
    );
    return 'web';
  }

  try {
    await navigator.clipboard.writeText(clipboardText);
    return 'copied';
  } catch {
    return 'failed';
  }
}

export function socialShareStatusMessage(
  platform: SocialSharePlatform,
  outcome: SocialShareOutcome,
  publicShareUrl?: string,
): string | null {
  if (outcome === 'aborted' || outcome === 'web') return null;
  const label = platformLabel(platform);

  if (outcome === 'shared') {
    return `Choose ${label} in the share menu to post your results, or paste from your clipboard if needed.`;
  }
  if (outcome === 'copied') {
    if (platform === 'facebook') {
      return 'Your message and link were copied — open Facebook and paste into a post, story, or message.';
    }
    return `${label} does not support one-click web sharing. Your message and link were copied — open the ${label} app and paste in a story, DM, or post.`;
  }
  return publicShareUrl
    ? `Could not copy automatically. Share this link: ${publicShareUrl}`
    : 'Could not copy automatically. Run Find matches again to generate a share link.';
}
